import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ExternalLink, Eye, Brain, Zap, Repeat } from "lucide-react";
import { ASSETS, LINKS, ARC } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import Typewriter from "@/components/Typewriter";
import { TermLabel, GridBackground } from "@/components/Effects";
import { useMizuWallet, useWalletUI, shortAddress } from "@/components/Wallet";
import CaChip from "@/components/CaChip";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

function HeroConnectButton() {
  const { t } = useLanguage();
  const { openModal } = useWalletUI();
  const w = useMizuWallet();
  return (
    <button
      data-testid="hero-connect-wallet"
      onClick={openModal}
      className="mizu-btn border border-mizu-cyan/50 bg-mizu-cyan/10 px-6 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
    >
      {w.isConnected ? shortAddress(w.address) : t("hero.connectWallet")}
    </button>
  );
}

export default function Home() {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 130]);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const loop = [
    { icon: Eye, label: "OBSERVE" },
    { icon: Brain, label: "THINK" },
    { icon: Zap, label: "EXECUTE" },
    { icon: Repeat, label: "REPEAT" },
  ];

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative flex min-h-[calc(100vh-4rem)] items-end overflow-hidden">
        <motion.img
          src={ASSETS.banner}
          alt="MIZUKARA banner artwork"
          data-testid="hero-banner"
          className="absolute inset-0 h-full w-full object-cover object-center"
          style={reduced ? undefined : { y: heroY, scale: 1.06 }}
        />
        {/* readability overlays — edges only, never a flat full-image gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#020611] via-[#020611]/25 to-[#020611]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020611]/85 via-transparent to-transparent" />
        <div className="mizu-vignette absolute inset-0" />

        <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 px-4 pb-16 pt-28 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          >
            <TermLabel data-testid="hero-label">{t("hero.label")}</TermLabel>

            <h1
              data-testid="hero-title"
              className="mt-5 font-display text-5xl font-black tracking-[0.1em] text-white drop-shadow-[0_0_25px_rgba(0,240,255,0.25)] sm:text-6xl lg:text-7xl"
            >
              MIZUKARA
            </h1>
            <div className="mt-2 font-jp text-lg tracking-[0.7em] text-mizu-cyan mizu-glow sm:text-xl">
              ミズカラ
            </div>

            <p
              data-testid="hero-statement"
              className={`mt-6 max-w-xl font-display text-xl font-bold leading-snug tracking-[0.08em] text-mizu-silver sm:text-2xl ${lang === "jp" ? "font-jp tracking-normal" : ""}`}
            >
              {t("hero.statement1")}
              <br />
              <span className="text-mizu-cyan mizu-glow">{t("hero.statement2")}</span>
            </p>
            <p className="mt-3 max-w-md font-mono text-xs leading-6 text-mizu-dim">
              {t("hero.sub")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/terminal"
                data-testid="hero-enter-terminal"
                className="mizu-btn border border-mizu-cyan bg-mizu-cyan/15 px-6 py-3 font-mono text-xs font-semibold tracking-[0.2em] text-white"
              >
                [ {t("hero.enterTerminal")} ]
              </Link>
              <HeroConnectButton />
              <a
                href={LINKS.buy}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy"
                className="mizu-btn flex items-center gap-1.5 border border-mizu-cyan/25 px-5 py-3 font-mono text-xs tracking-[0.2em] text-mizu-dim hover:text-white"
              >
                {t("hero.buy")} <ExternalLink size={11} />
              </a>
              <a
                href={LINKS.chart}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-chart"
                className="mizu-btn flex items-center gap-1.5 border border-mizu-cyan/25 px-5 py-3 font-mono text-xs tracking-[0.2em] text-mizu-dim hover:text-white"
              >
                {t("hero.chart")} <ExternalLink size={11} />
              </a>
            </div>

            <div className="mt-6">
              <CaChip testid="hero-ca" />
            </div>
          </motion.div>

          {/* hero terminal */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.4 }}
            className="mizu-panel mizu-corners hidden p-4 md:block"
            data-testid="hero-terminal"
          >
            <div className="mb-3 flex items-center gap-2 border-b border-mizu-cyan/15 pb-2">
              <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-mizu-cyan">
                {t("heroTerm.title")}
              </span>
            </div>
            <Typewriter
              lines={t("heroTerm.lines")}
              speed={16}
              lineDelay={160}
              startDelay={500}
              className="min-h-[180px] font-mono text-[11px] leading-6 text-mizu-dim"
            />
          </motion.div>
        </div>
      </section>

      {/* ============ AUTONOMOUS LOOP STRIP ============ */}
      <section className="relative border-y border-mizu-cyan/10 bg-mizu-surface/60 py-10">
        <GridBackground />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <TermLabel className="mb-6 text-center">{t("home.loopLabel")}</TermLabel>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
            {loop.map((item, i) => (
              <React.Fragment key={item.label}>
                <motion.div
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.12 }}
                  className="mizu-card flex items-center gap-3 border border-mizu-cyan/20 bg-mizu-elevated/40 px-5 py-3"
                  data-testid={`loop-${item.label.toLowerCase()}`}
                >
                  <item.icon size={16} className="text-mizu-cyan" />
                  <span className="font-display text-sm font-bold tracking-[0.25em] text-white">
                    {item.label}
                  </span>
                </motion.div>
                {i < loop.length - 1 && (
                  <ArrowRight size={14} className="text-mizu-cyan/50" aria-hidden />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ARC NETWORK ============ */}
      <section className="relative py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <motion.div {...fadeUp}>
            <TermLabel>{t("home.arcLabel")}</TermLabel>
            <h2 className={`mt-3 font-display text-2xl font-bold tracking-[0.1em] text-white sm:text-3xl ${lang === "jp" ? "font-jp tracking-wide" : ""}`}>
              {t("home.arcTitle")}
            </h2>
            <p className="mt-4 max-w-lg font-mono text-xs leading-7 text-mizu-dim">
              {t("home.arcDesc")}
            </p>
            <a
              href={LINKS.explorer}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-open-explorer"
              className="mizu-btn mt-7 inline-flex items-center gap-2 border border-mizu-cyan/50 bg-mizu-cyan/10 px-6 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
            >
              [ {t("home.openExplorer")} ] <ExternalLink size={11} />
            </a>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
            <div
              className="mizu-panel mizu-corners p-5 font-mono text-xs"
              data-testid="arc-panel"
            >
              <div className="mb-4 flex items-center justify-between border-b border-mizu-cyan/15 pb-2">
                <span className="text-[10px] tracking-[0.3em] text-mizu-cyan">
                  {t("home.arcLabel")}
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-mizu-cyan">
                  <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />
                  {t("home.available")}
                </span>
              </div>
              {[
                [t("home.network"), ARC.networkLabel],
                [t("home.chainId"), String(ARC.chainId)],
                [t("home.asset"), ARC.symbol],
                [t("home.rpc"), ARC.rpcHost],
                [t("home.explorer"), ARC.explorerHost],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between border-b border-mizu-cyan/8 py-2.5"
                >
                  <span className="tracking-[0.2em] text-mizu-faint">{k}</span>
                  <span className="text-mizu-silver">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ AGENT TEASER ============ */}
      <section className="relative border-t border-mizu-cyan/10 bg-mizu-surface/40 py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <motion.div {...fadeUp} className="relative order-2 lg:order-1">
            <div className="mizu-corners relative overflow-hidden border border-mizu-cyan/20">
              <img
                src={ASSETS.art}
                alt="MIZUKARA character artwork"
                data-testid="home-agent-art"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover object-[30%_15%] opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020611]/80 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.3em] text-mizu-cyan/90">
                {t("terminalPage.agentCardTitle")}
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="order-1 lg:order-2">
            <TermLabel>{t("home.agentLabel")}</TermLabel>
            <h2 className={`mt-3 font-display text-2xl font-bold tracking-[0.1em] text-white sm:text-3xl ${lang === "jp" ? "font-jp tracking-wide" : ""}`}>
              {t("home.agentTitle")}
            </h2>
            <p className="mt-4 max-w-lg font-mono text-xs leading-7 text-mizu-dim">
              {t("home.agentDesc")}
            </p>
            <Link
              to="/agent"
              data-testid="home-agent-cta"
              className="mizu-btn mt-7 inline-block border border-mizu-cyan/40 px-6 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
            >
              [ {t("home.agentCta")} ]
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============ LORE TEASER ============ */}
      <section className="relative overflow-hidden py-24">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${ASSETS.banner})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />
        <div className="absolute inset-0 bg-[#020611]/80" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <motion.div {...fadeUp}>
            <TermLabel className="justify-center">{t("home.loreLabel")}</TermLabel>
            <h2 className={`mt-4 font-display text-2xl font-bold tracking-[0.1em] text-white sm:text-3xl ${lang === "jp" ? "font-jp tracking-wide" : ""}`}>
              {t("home.loreTitle")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl font-mono text-xs leading-7 text-mizu-dim">
              {t("home.loreDesc")}
            </p>
            <Link
              to="/lore"
              data-testid="home-lore-cta"
              className="mizu-btn mt-8 inline-block border border-mizu-cyan/40 bg-[#020611]/60 px-6 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
            >
              [ {t("home.loreCta")} ]
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
